import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import {
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import EmployeesManagementPage from "../../pages/company/EmployeesManagementPage";

const EmployeeAnalytics = () => {
  const { t } = useTranslation();
  const { apiCall } = useAuth();

  // Новые состояния
  const [currentView, setCurrentView] = useState("analytics"); // 'list' или 'analytics'

  const [employees, setEmployees] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [timeRange, setTimeRange] = useState("30"); // days
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState(
    t("company.analytics.modes.overview"),
  ); // overview, individual, comparison

  useEffect(() => {
    loadEmployees();
    loadAnalytics();
  }, [timeRange]);

  const loadEmployees = async () => {
    try {
      const response = await apiCall("/companies/employees", "GET");
      if (response.success) {
        setEmployees(response.employees || []);
        if (response.employees?.length > 0 && !selectedEmployee) {
          setSelectedEmployee(response.employees[0]);
        }
      }
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  };

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await apiCall(
        `/companies/analytics/employees?days=${timeRange}`,
        "GET",
      );
      if (response.success) {
        setAnalytics(response.data || {});
      }
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mock data for demonstration
  const generateMockAnalytics = (employee) => {
    const baseMetrics = {
      totalBookings: Math.floor(Math.random() * 100) + 20,
      completedBookings: Math.floor(Math.random() * 80) + 15,
      cancelledBookings: Math.floor(Math.random() * 10) + 1,
      totalRevenue: Math.random() * 5000 + 1000,
      averageRating: Math.random() * 2 + 3,
      totalReviews: Math.floor(Math.random() * 50) + 5,
      hoursWorked: Math.floor(Math.random() * 160) + 40,
      utilizationRate: Math.random() * 40 + 60,
      responseTime: Math.floor(Math.random() * 30) + 5,
      repeatCustomers: Math.floor(Math.random() * 20) + 5,
      lastLogin: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
      weeklyTrend: Array.from(
        { length: 7 },
        () => Math.floor(Math.random() * 10) + 1,
      ),
    };

    return {
      ...baseMetrics,
      completionRate: (
        (baseMetrics.completedBookings / baseMetrics.totalBookings) *
        100
      ).toFixed(1),
      averageCheck: (
        baseMetrics.totalRevenue / baseMetrics.completedBookings
      ).toFixed(2),
      efficiency: (
        (baseMetrics.hoursWorked / (parseInt(timeRange) * 8)) *
        100
      ).toFixed(1),
    };
  };

  const getEmployeeMetrics = (employee) => {
    const defaultMetrics = {
      totalBookings: 0,
      completedBookings: 0,
      cancelledBookings: 0,
      totalRevenue: 0,
      averageRating: 0,
      completionRate: 0,
      utilizationRate: 0,
      lastLogin: null,
    };

    return analytics[employee.id] || defaultMetrics;
  };

  const getPerformanceColor = (value, thresholds) => {
    if (value >= thresholds.excellent) return "text-emerald-600";
    if (value >= thresholds.good) return "text-amber-500";
    return "text-rose-500";
  };

  const getPerformanceIcon = (value, thresholds) => {
    if (value >= thresholds.excellent) return ArrowTrendingUpIcon;
    if (value >= thresholds.good) return ArrowPathIcon;
    return ArrowTrendingDownIcon;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const MetricCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color = "text-slate-600",
    trend = null,
  }) => (
    <div className="bg-white p-6 rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] border border-slate-100/80 hover:-translate-y-1 transition-transform duration-300">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500 tracking-wide">
            {title}
          </p>
          <p className="text-3xl font-bold text-slate-800 mt-2">{value}</p>
          {subtitle && (
            <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div
          className={`flex-shrink-0 h-12 w-12 rounded-2xl flex items-center justify-center bg-slate-50 ${color}`}
        >
          <Icon className="h-6 w-6" strokeWidth={2} />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center">
          <div
            className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${trend > 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}
          >
            {trend > 0 ? (
              <ArrowTrendingUpIcon className="h-3 w-3 mr-1" strokeWidth={2.5} />
            ) : (
              <ArrowTrendingDownIcon
                className="h-3 w-3 mr-1"
                strokeWidth={2.5}
              />
            )}
            {Math.abs(trend)}%
          </div>
          <span className="text-xs text-slate-400 ml-2">
            {t("company.analytics.metrics.vs_last_period")}
          </span>
        </div>
      )}
    </div>
  );

  const EmployeeCard = ({ employee, metrics, onClick, isSelected }) => (
    <div
      className={`p-5 rounded-2xl border cursor-pointer transition-all duration-300 group ${
        isSelected
          ? "border-orange-200 bg-orange-50/30 shadow-[0_8px_30px_-4px_rgba(249,115,22,0.1)]"
          : "border-slate-100 hover:border-slate-200 bg-white shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.06)]"
      }`}
      onClick={() => onClick(employee)}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-4">
          <div
            className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors duration-300 ${isSelected ? "bg-orange-100 text-orange-600" : "bg-slate-50 text-slate-400 group-hover:bg-slate-100"}`}
          >
            <UserIcon className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-semibold text-slate-800 tracking-tight">
              {employee.first_name} {employee.last_name}
            </p>
            <p className="text-sm text-slate-400 capitalize font-medium">
              {employee.role}
            </p>
          </div>
        </div>
        <div className="flex items-center px-2.5 py-1 rounded-full bg-amber-50">
          <StarIcon
            className="h-4 w-4 text-amber-500 mr-1"
            fill="currentColor"
          />
          <span className="text-sm font-semibold text-amber-600">
            {metrics.averageRating.toFixed(1)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center bg-slate-50/50 p-3 rounded-xl border border-slate-100/50">
        <div>
          <p className="text-lg font-bold text-slate-800">
            {metrics.totalBookings}
          </p>
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mt-0.5">
            Bookings
          </p>
        </div>
        <div>
          <p className="text-lg font-bold text-emerald-600">
            {formatCurrency(metrics.totalRevenue)}
          </p>
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mt-0.5">
            Revenue
          </p>
        </div>
        <div>
          <p className="text-lg font-bold text-indigo-600">
            {metrics.completionRate}%
          </p>
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mt-0.5">
            Completed
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-400 flex items-center">
          <ClockIcon className="h-3.5 w-3.5 mr-1" />
          {formatDate(metrics.lastLogin)}
        </span>
        <span
          className={`px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide uppercase ${
            employee.is_active
              ? "bg-emerald-50 text-emerald-600"
              : "bg-rose-50 text-rose-600"
          }`}
        >
          {employee.is_active
            ? t("company.analytics.status.active")
            : t("company.analytics.status.inactive")}
        </span>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-orange-500 mb-4"></div>
          <p className="text-slate-500 font-medium tracking-wide">
            {t("company.analytics.comparison.status.loading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] font-sans selection:bg-orange-100 selection:text-orange-900 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header Section с новыми кнопками */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center tracking-tight">
              <div className="h-10 w-10 bg-white shadow-sm border border-slate-100 rounded-xl flex items-center justify-center mr-4">
                {/* Динамическая иконка в зависимости от вида */}
                {currentView === "analytics" ? (
                  <ChartBarIcon
                    className="h-6 w-6 text-orange-500"
                    strokeWidth={2}
                  />
                ) : (
                  <UserIcon className="h-6 w-6 text-blue-500" strokeWidth={2} />
                )}
              </div>
              {/* ДИНАМИЧЕСКИЙ ЗАГОЛОВОК */}
              {currentView === "analytics"
                ? t("company.analytics.title")
                : t("company.employees.title")}
            </h1>
            <p className="mt-2 text-slate-500 font-medium ml-14">
              {currentView === "analytics"
                ? t("company.analytics.subtitle_analytics")
                : t("company.analytics.subtitle_management")}
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex bg-zinc-100 p-1.5 rounded-[20px]">
              <button
                onClick={() => setCurrentView("list")}
                className={`px-6 py-2 text-sm font-bold rounded-2xl transition-all ${
                  currentView === "list"
                    ? "bg-white shadow-sm text-zinc-900"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                {t("company.analytics.tabs.list")}
              </button>
              <button
                onClick={() => setCurrentView("analytics")}
                className={`px-6 py-2 text-sm font-bold rounded-2xl transition-all ${
                  currentView === "analytics"
                    ? "bg-white shadow-sm text-zinc-900"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                <ChartBarIcon className="h-4 w-4 inline mr-2" />
                {t("company.analytics.tabs.analytics")}
              </button>
            </div>
          </div>
        </div>

        {/* --- КОНТЕНТ В ЗАВИСИМОСТИ ОТ ВЫБОРА --- */}
        {currentView === "list" ? (
          <EmployeesManagementPage />
        ) : (
          /* АНАЛИТИКА */
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Панель управления аналитикой (Time & View Mode) */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-4 mb-8">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all cursor-pointer appearance-none pr-10 relative"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: `right 0.5rem center`,
                  backgroundRepeat: `no-repeat`,
                  backgroundSize: `1.5em 1.5em`,
                }}
              >
                <option value="7">
                  {t("company.analytics.time_ranges.7")}
                </option>
                <option value="30">
                  {t("company.analytics.time_ranges.30")}
                </option>
                <option value="90">
                  {t("company.analytics.time_ranges.90")}
                </option>
                <option value="365">
                  {t("company.analytics.time_ranges.365")}
                </option>
              </select>

              <div className="flex bg-slate-200/50 p-1 rounded-xl w-full sm:w-auto">
                {[
                  t("company.analytics.modes.overview"),
                  t("company.analytics.modes.individual"),
                  t("company.analytics.modes.comparison"),
                ].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`flex-1 sm:flex-none px-5 py-2 text-sm font-semibold capitalize rounded-lg transition-all duration-200 ${
                      viewMode === mode
                        ? "bg-white text-slate-800 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.08)]"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Mode */}
            {viewMode === t("company.analytics.modes.overview") && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <MetricCard
                    title={t("company.analytics.metrics.total_employees")}
                    value={employees.length}
                    subtitle={
                      `${employees.filter((e) => e.is_active).length} ` +
                      t("company.analytics.metrics.active_members")
                    }
                    icon={UserIcon}
                    color="text-indigo-500"
                  />
                  <MetricCard
                    title={t("company.analytics.metrics.total_bookings")}
                    value={employees.reduce(
                      (sum, emp) => sum + getEmployeeMetrics(emp).totalBookings,
                      0,
                    )}
                    subtitle={t("company.analytics.metrics.period_label")}
                    icon={CalendarDaysIcon}
                    color="text-emerald-500"
                    trend={12}
                  />
                  <MetricCard
                    title={t("company.analytics.metrics.total_revenue")}
                    value={formatCurrency(
                      employees.reduce(
                        (sum, emp) =>
                          sum + getEmployeeMetrics(emp).totalRevenue,
                        0,
                      ),
                    )}
                    subtitle={t("company.analytics.metrics.team_generated")}
                    icon={CurrencyDollarIcon}
                    color="text-orange-500"
                    trend={8}
                  />
                  <MetricCard
                    title={t("company.analytics.metrics.avg_rating")}
                    value={(
                      employees.reduce(
                        (sum, emp) =>
                          sum + getEmployeeMetrics(emp).averageRating,
                        0,
                      ) / (employees.length || 1)
                    ).toFixed(1)}
                    subtitle="Overall satisfaction"
                    icon={StarIcon}
                    color="text-amber-500"
                    trend={-2}
                  />
                </div>

                <div className="bg-white rounded-3xl shadow-[0_4px_24px_-6px_rgba(0,0,0,0.04)] border border-slate-100/60 p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                      Team Performance
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {employees.map((employee) => (
                      <EmployeeCard
                        key={employee.id}
                        employee={employee}
                        metrics={getEmployeeMetrics(employee)}
                        onClick={setSelectedEmployee}
                        isSelected={selectedEmployee?.id === employee.id}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Individual Mode */}
            {viewMode === t("company.analytics.modes.individual") &&
              selectedEmployee && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="bg-white rounded-3xl shadow-[0_4px_24px_-6px_rgba(0,0,0,0.04)] border border-slate-100/60 p-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div className="flex items-center">
                        <div className="h-16 w-16 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center mr-5 border border-orange-100">
                          <UserIcon className="h-8 w-8" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                            {selectedEmployee.first_name}{" "}
                            {selectedEmployee.last_name}
                          </h2>
                          <div className="flex items-center mt-1 space-x-3 text-sm">
                            <span className="font-semibold text-slate-500 capitalize">
                              {selectedEmployee.role}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-500 capitalize">
                              {selectedEmployee.department || "General"}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-400">
                              @{selectedEmployee.username}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="relative">
                        <select
                          value={selectedEmployee.id}
                          onChange={(e) =>
                            setSelectedEmployee(
                              employees.find(
                                (emp) => emp.id === e.target.value,
                              ),
                            )
                          }
                          className="w-full md:w-64 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 focus:bg-white transition-all cursor-pointer appearance-none pr-10"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23475569' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: `right 1rem center`,
                            backgroundRepeat: `no-repeat`,
                            backgroundSize: `1.5em 1.5em`,
                          }}
                        >
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {t("company.analytics.status.switch_to")}{" "}
                              {emp.first_name} {emp.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const metrics = getEmployeeMetrics(selectedEmployee);
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <MetricCard
                          title={t(
                            "company.analytics.metrics.bookings_completed",
                          )}
                          value={metrics.completedBookings}
                          subtitle={
                            `${metrics.completionRate}% ` +
                            t("company.analytics.metrics.completion_rate")
                          }
                          icon={CheckCircleIcon}
                          color="text-emerald-500"
                        />
                        <MetricCard
                          title={t(
                            "company.analytics.metrics.revenue_generated",
                          )}
                          value={formatCurrency(metrics.totalRevenue)}
                          subtitle={
                            `${formatCurrency(metrics.averageCheck)} ` +
                            t("company.analytics.metrics.avf_check")
                          }
                          icon={CurrencyDollarIcon}
                          color="text-indigo-500"
                        />
                        <MetricCard
                          title={t("company.analytics.metrics.customer_rating")}
                          value={`${metrics.averageRating.toFixed(1)}`}
                          subtitle={
                            `${metrics.totalReviews} ` +
                            t("company.analytics.metrics.total_reviews")
                          }
                          icon={StarIcon}
                          color="text-amber-500"
                        />
                        <MetricCard
                          title={t("company.analytics.metrics.utilization")}
                          value={`${metrics.utilizationRate.toFixed(1)}%`}
                          subtitle={
                            `${metrics.hoursWorked} ` +
                            t("company.analytics.metrics.hours_tracked")
                          }
                          icon={ClockIcon}
                          color="text-sky-500"
                        />
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-white rounded-3xl shadow-[0_4px_24px_-6px_rgba(0,0,0,0.04)] border border-slate-100/60 p-8">
                      <h3 className="text-lg font-bold text-slate-800 mb-6 tracking-tight">
                        {t("company.analytics.indicators.title")}
                      </h3>
                      {(() => {
                        const metrics = getEmployeeMetrics(selectedEmployee);
                        const indicators = [
                          {
                            name: t(
                              "company.analytics.indicators.completion_rate",
                            ),
                            value: parseFloat(metrics.completionRate),
                            thresholds: { excellent: 90, good: 75 },
                            unit: "%",
                          },
                          {
                            name: t(
                              "company.analytics.indicators.customer_sat",
                            ),
                            value: metrics.averageRating,
                            thresholds: { excellent: 4.5, good: 4.0 },
                            unit: "/5",
                          },
                          {
                            name: t(
                              "company.analytics.indicators.response_time",
                            ),
                            value: metrics.responseTime,
                            thresholds: { excellent: 10, good: 20 },
                            unit: "m",
                            reverse: true,
                          },
                          {
                            name: t(
                              "company.analytics.indicators.utilization_rate",
                            ),
                            value: parseFloat(metrics.utilizationRate),
                            thresholds: { excellent: 80, good: 60 },
                            unit: "%",
                          },
                        ];

                        return (
                          <div className="space-y-4">
                            {indicators.map((indicator, index) => {
                              const thresholds = indicator.reverse
                                ? {
                                    excellent: indicator.thresholds.good,
                                    good: indicator.thresholds.excellent,
                                  }
                                : indicator.thresholds;
                              const color = getPerformanceColor(
                                indicator.value,
                                thresholds,
                              );
                              const Icon = getPerformanceIcon(
                                indicator.value,
                                thresholds,
                              );
                              const bgColor = color.includes("emerald")
                                ? "bg-emerald-50"
                                : color.includes("amber")
                                  ? "bg-amber-50"
                                  : "bg-rose-50";

                              return (
                                <div
                                  key={index}
                                  className="group flex items-center justify-between p-4 rounded-2xl border border-slate-100/50 hover:bg-slate-50 transition-colors"
                                >
                                  <div className="flex items-center">
                                    <div
                                      className={`p-2 rounded-xl mr-4 ${bgColor}`}
                                    >
                                      <Icon
                                        className={`h-5 w-5 ${color}`}
                                        strokeWidth={2}
                                      />
                                    </div>
                                    <span className="font-semibold text-slate-700">
                                      {indicator.name}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-lg font-bold ${color}`}
                                  >
                                    {indicator.value}
                                    {indicator.unit}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="bg-white rounded-3xl shadow-[0_4px_24px_-6px_rgba(0,0,0,0.04)] border border-slate-100/60 p-8">
                      <h3 className="text-lg font-bold text-slate-800 mb-6 tracking-tight">
                        Activity Breakdown
                      </h3>
                      {(() => {
                        const metrics = getEmployeeMetrics(selectedEmployee);
                        return (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100/50">
                              <div className="flex items-center">
                                <CheckCircleIcon
                                  className="h-6 w-6 text-emerald-500 mr-4"
                                  strokeWidth={1.5}
                                />
                                <span className="font-semibold text-slate-700">
                                  {t("company.analytics.activity.successful")}
                                </span>
                              </div>
                              <span className="text-xl font-bold text-emerald-600">
                                {metrics.completedBookings}
                              </span>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100/50">
                              <div className="flex items-center">
                                <XCircleIcon
                                  className="h-6 w-6 text-rose-500 mr-4"
                                  strokeWidth={1.5}
                                />
                                <span className="font-semibold text-slate-700">
                                  {t("company.analytics.activity.cancelled")}
                                </span>
                              </div>
                              <span className="text-xl font-bold text-rose-600">
                                {metrics.cancelledBookings}
                              </span>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100/50">
                              <div className="flex items-center">
                                <UserIcon
                                  className="h-6 w-6 text-indigo-500 mr-4"
                                  strokeWidth={1.5}
                                />
                                <span className="font-semibold text-slate-700">
                                  {t("company.analytics.activity.returning")}
                                </span>
                              </div>
                              <span className="text-xl font-bold text-indigo-600">
                                {metrics.repeatCustomers}
                              </span>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100/50">
                              <div className="flex items-center">
                                <ClockIcon
                                  className="h-6 w-6 text-slate-400 mr-4"
                                  strokeWidth={1.5}
                                />
                                <span className="font-semibold text-slate-700">
                                  {t("company.analytics.activity.last_login")}
                                </span>
                              </div>
                              <span className="font-medium text-slate-500">
                                {formatDate(metrics.lastLogin)}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

            {/* Comparison Mode */}
            {viewMode === t("company.analytics.modes.comparison") && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white rounded-3xl shadow-[0_4px_24px_-6px_rgba(0,0,0,0.04)] border border-slate-100/60 overflow-hidden">
                  <div className="px-8 py-6 border-b border-slate-100">
                    <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                      {t("company.analytics.comparison.title")}
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100">
                      <thead className="bg-slate-50/50">
                        <tr>
                          <th className="px-8 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t("company.analytics.comparison.table.employee")}
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t("company.analytics.comparison.table.bookings")}
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t("company.analytics.comparison.table.revenue")}
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t("company.analytics.comparison.table.rating")}
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t("company.analytics.comparison.table.completion")}
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t(
                              "company.analytics.comparison.table.utilization",
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-100">
                        {employees.map((employee) => {
                          const metrics = getEmployeeMetrics(employee);
                          return (
                            <tr
                              key={employee.id}
                              className="hover:bg-slate-50/80 transition-colors group"
                            >
                              <td className="px-8 py-5 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center mr-4 group-hover:bg-orange-50 group-hover:text-orange-500 transition-colors">
                                    <span className="text-sm font-bold">
                                      {employee.first_name[0]}
                                      {employee.last_name[0]}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-slate-800">
                                      {employee.first_name} {employee.last_name}
                                    </div>
                                    <div className="text-xs font-medium text-slate-400 capitalize mt-0.5">
                                      {employee.role}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                <span className="text-sm font-bold text-slate-700">
                                  {metrics.totalBookings}
                                </span>
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                <span className="text-sm font-bold text-emerald-600">
                                  {formatCurrency(metrics.totalRevenue)}
                                </span>
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                <div className="flex items-center">
                                  <StarIcon
                                    className="h-4 w-4 text-amber-500 mr-1.5"
                                    fill="currentColor"
                                  />
                                  <span className="text-sm font-bold text-slate-700">
                                    {metrics.averageRating.toFixed(1)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                <span className="text-sm font-bold text-slate-700">
                                  {metrics.completionRate}%
                                </span>
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                <span className="text-sm font-bold text-slate-700">
                                  {metrics.utilizationRate.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeAnalytics;
